import { Loading } from '@anytag/shared-atoms';
import { VideoItem, Rule } from '../../types';

interface AnalysisSummaryTableProps {
  items: VideoItem[];
  rules: Rule[];
  isLoading: boolean;
}

export const AnalysisSummaryTable = ({ items, rules, isLoading }: AnalysisSummaryTableProps) => {
  const doneItems = items.filter((i) => i.status === 'DONE' && i.result);

  const getInfluencerName = (fileName: string) => (fileName ? fileName.replace(/\.mp4$/i, '') : 'Unknown Video');

  return (
    <div className="inline-block min-w-full align-middle">
      {doneItems.length === 0 && !isLoading ? (
        <div className="py-10 text-center text-tertiary">
          {items.length === 0 ? 'No analyzed videos yet.' : 'Ready to analyze.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border-base-border">
          <table className="min-w-full divide-y divide-black-50">
            <thead className="bg-black-10">
              <tr>
                <th
                  scope="col"
                  className="w-1/5 px-6 py-3 text-left text-xs font-bold tracking-wider text-primary uppercase"
                >
                  Video / Influencer
                </th>
                <th
                  scope="col"
                  className="w-1/4 px-6 py-3 text-left text-xs font-bold tracking-wider text-attention-green uppercase"
                >
                  Do (Followed Guidelines)
                </th>
                <th
                  scope="col"
                  className="w-1/4 px-6 py-3 text-left text-xs font-bold tracking-wider text-attention-red uppercase"
                >
                  Don't (Violated Guidelines)
                </th>
                <th
                  scope="col"
                  className="w-1/4 px-6 py-3 text-left text-xs font-bold tracking-wider text-attention-yellow uppercase"
                >
                  Suggestion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black-50 bg-white">
              {doneItems.map((item) => {
                const res = item.result;
                const passedRules = res?.ruleBreakdown.filter((r) => r.status === 'PASS');
                const failedRules = res?.ruleBreakdown.filter((r) => r.status === 'FAIL');

                return (
                  <tr key={item.id} className="transition-colors hover:bg-black-10">
                    <td className="px-6 py-4 align-top text-sm font-medium whitespace-nowrap text-primary">
                      {getInfluencerName(item.fileName)}
                    </td>
                    <td className="px-6 py-4 align-top text-sm text-tertiary">
                      <ul className="list-disc space-y-1 pl-4">
                        {passedRules?.length && passedRules.length > 0 ? (
                          passedRules.map((r, idx) => {
                            const ruleText = rules.find((ru) => ru.id === r.ruleId)?.text || 'Unknown';
                            return (
                              <li key={idx} className="text-attention-green/30">
                                <span className="text-primary">{ruleText}</span>
                                {r.timestampStart && (
                                  <span className="ml-1 font-mono text-xxs text-tertiary">
                                    [{r.timestampStart}
                                    {r.timestampEnd ? `-${r.timestampEnd}` : ''}]
                                  </span>
                                )}
                              </li>
                            );
                          })
                        ) : (
                          <li className="text-tertiary italic">None</li>
                        )}
                      </ul>
                    </td>
                    <td className="px-6 py-4 align-top text-sm text-tertiary">
                      <ul className="list-disc space-y-1 pl-4">
                        {failedRules?.length && failedRules.length > 0 ? (
                          failedRules.map((r, idx) => {
                            const ruleText = rules.find((ru) => ru.id === r.ruleId)?.text || 'Unknown';
                            return (
                              <li key={idx} className="text-attention-red/30">
                                <span className="text-primary">{ruleText}</span>
                                {r.timestampStart && (
                                  <span className="ml-1 font-mono text-xxs text-tertiary">
                                    [{r.timestampStart}
                                    {r.timestampEnd ? `-${r.timestampEnd}` : ''}]
                                  </span>
                                )}
                              </li>
                            );
                          })
                        ) : (
                          <li className="text-tertiary italic">None</li>
                        )}
                      </ul>
                    </td>
                    <td className="px-6 py-4 align-top text-sm whitespace-pre-wrap text-attention-yellow">
                      {res?.suggestions || 'No specific suggestions.'}
                    </td>
                  </tr>
                );
              })}

              {isLoading &&
                (doneItems.length === 0 ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <tr key={idx} className="animate-pulse border-base-border last:border-0">
                      <td className="px-6 py-4 align-top">
                        <div className="mb-2 h-4 w-3/4 rounded bg-black-10"></div>
                        <div className="h-3 w-1/2 rounded bg-black-10"></div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="mb-2 h-3 w-full rounded bg-attention-green/20"></div>
                        <div className="mb-2 h-3 w-5/6 rounded bg-attention-green/20"></div>
                        <div className="h-3 w-2/3 rounded bg-attention-green/20"></div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="mb-2 h-3 w-full rounded bg-attention-red/20"></div>
                        <div className="mb-2 h-3 w-4/5 rounded bg-attention-red/20"></div>
                        <div className="h-3 w-3/4 rounded bg-attention-red/20"></div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="h-16 w-full rounded bg-attention-yellow/50"></div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="bg-black-10/30 px-6 py-6 text-center">
                      <Loading className="h-50">
                        <span className="mt-4 animate-pulse text-sm font-medium text-tertiary">
                          Analyzing next video...
                        </span>
                      </Loading>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
